// Marketing module: email campaigns, segments, and nurture journeys.
// Journey definitions are stored as a JSON node graph on MarketingJourney.definition;
// the API-side runner and the web builder both consume these shapes.

export type MarketingSegmentKind = 'DYNAMIC' | 'STATIC'

export type MarketingCampaignStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'SCHEDULED'
  | 'SENDING'
  | 'SENT'
  | 'PAUSED'
  | 'CANCELLED'
  | 'FAILED'

export type MarketingSendStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'SENT'
  | 'DELIVERED'
  | 'OPENED'
  | 'CLICKED'
  | 'BOUNCED'
  | 'COMPLAINED'
  | 'UNSUBSCRIBED'
  | 'SUPPRESSED'
  | 'FAILED'
  | 'CANCELLED'

export type MarketingJourneyStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED'

export type MarketingEnrollmentStatus =
  | 'ACTIVE'
  | 'COMPLETED'
  | 'GOAL_MET'
  | 'EXITED'
  | 'FAILED'
  | 'CANCELLED'

export type MarketingOptOutReason = 'UNSUBSCRIBED' | 'MANUAL' | 'IMPORT'

export type MarketingConsentStatus =
  | 'GRANTED'
  | 'WITHDRAWN'
  | 'EXPIRED'
  | 'REVOKED'

export type MarketingApprovalStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'

export type MarketingSesProvisioningStatus =
  | 'NOT_CONFIGURED'
  | 'PROVISIONING'
  | 'READY'
  | 'PAUSED'
  | 'FAILED'

export type MarketingSendingDomainStatus =
  | 'PENDING'
  | 'VERIFIED'
  | 'FAILED'
  | 'TEMPORARY_FAILURE'

export type MarketingJourneyTriggerType =
  | 'MANUAL'
  | 'SEGMENT'
  | 'FORM_SUBMISSION'
  | 'STAGE_CHANGE'

export type MarketingWinnerMetric = 'OPEN' | 'CLICK'

export interface MarketingAbConfig {
  /** % of the audience used for the A/B test phase (split evenly between variants) */
  samplePercent: number
  winnerMetric: MarketingWinnerMetric
  /** Hours after the test phase starts before the winner is picked */
  decisionHours: number
}

// ---------------------------------------------------------------------------
// Journey node graph
// ---------------------------------------------------------------------------

export type MarketingJourneyNodeType =
  | 'TRIGGER'
  | 'SEND_EMAIL'
  | 'WAIT'
  | 'BRANCH'
  | 'UPDATE_CONTACT'
  | 'END'

export interface MarketingJourneyNodeBase {
  id: string
  type: MarketingJourneyNodeType
  /** Canvas position (React Flow) */
  position?: { x: number; y: number }
}

export interface MarketingTriggerNode extends MarketingJourneyNodeBase {
  type: 'TRIGGER'
}

export interface MarketingSendEmailNode extends MarketingJourneyNodeBase {
  type: 'SEND_EMAIL'
  config: {
    subject: string
    preheader?: string
    contentHtml: string
    contentJson?: unknown
    fromName?: string
    fromEmail?: string
    replyTo?: string
  }
}

export interface MarketingWaitNode extends MarketingJourneyNodeBase {
  type: 'WAIT'
  config: {
    /** Fixed wait */
    days?: number
    hours?: number
    minutes?: number
    /** Or wait until a local hour of day (0-23) in the journey timezone */
    untilHour?: number
  }
}

export type MarketingBranchCondition =
  | {
      kind: 'EMAIL_OPENED' | 'EMAIL_CLICKED'
      /** Node id of a prior SEND_EMAIL node; defaults to the most recent send */
      emailNodeId?: string
      /** How long to keep waiting for the engagement before taking the "no" path */
      windowHours: number
    }
  | {
      kind: 'CONTACT_QUERY'
      /** ScQL over CONTACT fields, evaluated immediately */
      query: string
    }

export interface MarketingBranchNode extends MarketingJourneyNodeBase {
  type: 'BRANCH'
  config: MarketingBranchCondition
}

export interface MarketingUpdateContactNode extends MarketingJourneyNodeBase {
  type: 'UPDATE_CONTACT'
  config: {
    /** CrmStage.stageId for CONTACT_LIFECYCLE to move the contact to */
    lifecycleStage?: string
    /** Score delta to apply (positive or negative) */
    scoreDelta?: number
    /** Tag-style source value to set if empty */
    source?: string
  }
}

export interface MarketingEndNode extends MarketingJourneyNodeBase {
  type: 'END'
}

export type MarketingJourneyNode =
  | MarketingTriggerNode
  | MarketingSendEmailNode
  | MarketingWaitNode
  | MarketingBranchNode
  | MarketingUpdateContactNode
  | MarketingEndNode

export interface MarketingJourneyEdge {
  id: string
  source: string
  target: string
  /** BRANCH nodes emit 'yes' / 'no'; all other nodes emit a single unlabeled edge */
  sourceHandle?: 'yes' | 'no' | null
}

export interface MarketingJourneyDefinition {
  nodes: MarketingJourneyNode[]
  edges: MarketingJourneyEdge[]
}

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export interface MarketingSegmentDTO {
  id: string
  organizationId: string
  name: string
  description: string | null
  kind: MarketingSegmentKind
  query: string | null
  memberCount: number
  countUpdatedAt: string | null
  createdById: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateMarketingSegmentDTO {
  name: string
  description?: string
  kind?: MarketingSegmentKind
  query?: string
}

export type UpdateMarketingSegmentDTO = Partial<CreateMarketingSegmentDTO>

export interface MarketingCampaignVariantDTO {
  id: string
  campaignId: string
  key: string
  subject: string
  preheader: string | null
  contentHtml: string
  contentJson: unknown
  isWinner: boolean
  sentCount: number
  openCount: number
  clickCount: number
}

export interface MarketingCampaignDTO {
  id: string
  organizationId: string
  name: string
  status: MarketingCampaignStatus
  segmentId: string | null
  segmentName?: string | null
  audienceQuery: string | null
  fromName: string | null
  fromEmail: string | null
  replyTo: string | null
  consentPurpose: string
  consentListKey: string | null
  scheduledAt: string | null
  timezone: string | null
  sendTimeOptimization: boolean
  abEnabled: boolean
  abConfig: MarketingAbConfig | null
  abWinnerVariantId: string | null
  abDecideAt: string | null
  sendStartedAt: string | null
  sentAt: string | null
  approvalRecipientCount: number | null
  approvedAudienceHash: string | null
  approvedAt: string | null
  approvedById: string | null
  recipientCount: number
  sentCount: number
  deliveredCount: number
  openCount: number
  clickCount: number
  bounceCount: number
  complaintCount: number
  unsubscribeCount: number
  failedCount: number
  suppressedCount: number
  variants: MarketingCampaignVariantDTO[]
  createdById: string | null
  createdAt: string
  updatedAt: string
}

export interface MarketingCampaignVariantInput {
  key: string
  subject?: string
  preheader?: string
  contentHtml?: string
  contentJson?: unknown
}

export interface CreateMarketingCampaignDTO {
  name: string
  /** null clears a previously selected segment */
  segmentId?: string | null
  audienceQuery?: string
  fromName?: string
  fromEmail?: string
  replyTo?: string
  consentPurpose?: string
  consentListKey?: string | null
  sendTimeOptimization?: boolean
  abEnabled?: boolean
  abConfig?: MarketingAbConfig
  variants?: MarketingCampaignVariantInput[]
}

export type UpdateMarketingCampaignDTO = Partial<CreateMarketingCampaignDTO>

export interface ScheduleMarketingCampaignDTO {
  /** ISO timestamp; omit to send immediately */
  scheduledAt?: string
  timezone?: string
}

export interface MarketingJourneyDTO {
  id: string
  organizationId: string
  name: string
  description: string | null
  status: MarketingJourneyStatus
  definition: MarketingJourneyDefinition
  triggerType: MarketingJourneyTriggerType
  triggerSegmentId: string | null
  triggerSegmentName?: string | null
  triggerConfig: { formId?: string; stageId?: string } | null
  exitQuery: string | null
  goalQuery: string | null
  allowReEnrollment: boolean
  quietHoursStart: number | null
  quietHoursEnd: number | null
  timezone: string | null
  enrolledCount: number
  activeCount: number
  completedCount: number
  goalMetCount: number
  exitedCount: number
  activatedAt: string | null
  createdById: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateMarketingJourneyDTO {
  name: string
  description?: string
  definition?: MarketingJourneyDefinition
  triggerType?: MarketingJourneyTriggerType
  /** null clears a previously selected segment */
  triggerSegmentId?: string | null
  triggerConfig?: { formId?: string; stageId?: string }
  exitQuery?: string
  goalQuery?: string
  allowReEnrollment?: boolean
  quietHoursStart?: number
  quietHoursEnd?: number
  timezone?: string
}

export type UpdateMarketingJourneyDTO = Partial<CreateMarketingJourneyDTO>

export interface MarketingRecipientDTO {
  id: string
  contactId: string
  contactName?: string
  email: string
  status: MarketingSendStatus
  variantKey?: string | null
  sentAt: string | null
  firstOpenedAt: string | null
  firstClickedAt: string | null
  openCount: number
  clickCount: number
  error: string | null
}

export interface MarketingEnrollmentDTO {
  id: string
  journeyId: string
  contactId: string
  contactName?: string
  contactEmail?: string | null
  status: MarketingEnrollmentStatus
  currentNodeId: string | null
  nextRunAt: string | null
  enteredAt: string
  exitedAt: string | null
  exitReason: string | null
}

export interface MarketingSegmentPreviewDTO {
  count: number
  sample: Array<{
    id: string
    name: string
    email: string | null
    lifecycleStage: string
  }>
}

export interface MarketingOptOutDTO {
  id: string
  email: string
  reason: MarketingOptOutReason
  sourceCampaignId: string | null
  createdAt: string
}

export interface MarketingEmailTemplateDTO {
  id: string
  organizationId: string
  name: string
  subject: string | null
  contentHtml: string
  contentJson: unknown
  createdById: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateMarketingEmailTemplateDTO {
  name: string
  subject?: string
  contentHtml: string
  contentJson?: unknown
}

export type UpdateMarketingEmailTemplateDTO =
  Partial<CreateMarketingEmailTemplateDTO>

export interface MarketingLinkStatDTO {
  id: string
  url: string
  clickCount: number
  /** Journey email step that contained the link (journeys only) */
  nodeId: string | null
}

export interface MarketingOverviewStatsDTO {
  contactsReachable: number
  optedOut: number
  suppressed: number
  campaignsSent: number
  avgOpenRate: number
  avgClickRate: number
  activeJourneys: number
  activeEnrollments: number
}

export interface MarketingComplianceProfileDTO {
  legalName: string
  addressLine1: string
  addressLine2: string | null
  city: string
  region: string | null
  postalCode: string
  countryCode: string
  contactEmail: string
  senderName: string
  senderLocalPart: string
  advertisementDisclosure: string
  preferenceCenterUrl: string | null
  defaultPurpose: string
  disclosureVersion: string
  approvalThreshold: number
  hourlySendLimit: number
  dailySendLimit: number
  maxMessageBytes: number
  autoPauseBounceRate: number
  autoPauseComplaintRate: number
  updatedAt: string
}

export type UpdateMarketingComplianceProfileDTO = Omit<
  MarketingComplianceProfileDTO,
  'updatedAt'
>

export interface CreateMarketingConsentEventDTO {
  email: string
  purpose: string
  listKey?: string | null
  status: MarketingConsentStatus
  source: string
  occurredAt?: string
  ipAddress?: string
  userAgent?: string
  disclosureVersion: string
  jurisdiction?: string
  lawfulBasis: string
  evidence: Record<string, unknown>
}

export interface MarketingConsentEventDTO extends CreateMarketingConsentEventDTO {
  id: string
  createdById: string | null
  createdAt: string
}

export interface ResolveMarketingApprovalDTO {
  approved: boolean
  note?: string
}

export interface MarketingDnsRecordDTO {
  type: 'CNAME' | 'MX' | 'TXT'
  name: string
  value: string
  purpose: 'DKIM' | 'MAIL_FROM_MX' | 'MAIL_FROM_SPF'
}

export interface MarketingSendingDomainDTO {
  id: string
  domain: string
  mailFromDomain: string
  status: MarketingSendingDomainStatus
  isDefault: boolean
  dnsRecords: MarketingDnsRecordDTO[]
  verifiedAt: string | null
  lastCheckedAt: string | null
  lastError: string | null
}

export interface MarketingSendingSetupDTO {
  managementAvailable: boolean
  status: MarketingSesProvisioningStatus
  sendingStatus: 'ENABLED' | 'REINSTATED' | 'DISABLED' | null
  platformDomain: string
  activeDomain: string
  domains: MarketingSendingDomainDTO[]
  lastError: string | null
}

// ---------------------------------------------------------------------------
// Validation helpers shared by API + web builder
// ---------------------------------------------------------------------------

export interface MarketingJourneyValidationIssue {
  nodeId?: string
  message: string
}

/**
 * Structural validation of a journey graph: exactly one trigger, all nodes
 * reachable, no cycles, sends non-empty, branch windows sane. Used by the web
 * builder (inline feedback) and the API (activation gate) — keep in shared.
 */
export function validateMarketingJourneyDefinition(
  definition: MarketingJourneyDefinition,
): MarketingJourneyValidationIssue[] {
  const issues: MarketingJourneyValidationIssue[] = []
  const nodes = definition?.nodes ?? []
  const edges = definition?.edges ?? []

  const triggers = nodes.filter(n => n.type === 'TRIGGER')
  if (triggers.length !== 1) {
    issues.push({
      message:
        triggers.length === 0
          ? 'Journey needs a trigger node'
          : 'Journey can only have one trigger node',
    })
  }

  const nodeIds = new Set(nodes.map(n => n.id))
  for (const edge of edges) {
    if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) {
      issues.push({ message: 'Journey has a connection to a missing step' })
      break
    }
  }

  // Reachability from the trigger
  if (triggers.length === 1) {
    const adjacency = new Map<string, string[]>()
    for (const edge of edges) {
      const list = adjacency.get(edge.source) ?? []
      list.push(edge.target)
      adjacency.set(edge.source, list)
    }
    const visited = new Set<string>()
    const queue = [triggers[0].id]
    while (queue.length) {
      const current = queue.shift() as string
      if (visited.has(current)) continue
      visited.add(current)
      for (const next of adjacency.get(current) ?? []) queue.push(next)
    }
    for (const node of nodes) {
      if (!visited.has(node.id)) {
        issues.push({
          nodeId: node.id,
          message: 'Step is not connected to the journey',
        })
      }
    }

    // Cycle detection (DFS with colors) — journeys must be a DAG
    const color = new Map<string, number>() // 0 unvisited, 1 in-stack, 2 done
    const hasCycle = (id: string): boolean => {
      color.set(id, 1)
      for (const next of adjacency.get(id) ?? []) {
        const c = color.get(next) ?? 0
        if (c === 1) return true
        if (c === 0 && hasCycle(next)) return true
      }
      color.set(id, 2)
      return false
    }
    if (hasCycle(triggers[0].id)) {
      issues.push({ message: 'Journey steps cannot loop back on themselves' })
    }
  }

  for (const node of nodes) {
    if (node.type === 'SEND_EMAIL') {
      if (!node.config?.subject?.trim()) {
        issues.push({ nodeId: node.id, message: 'Email step needs a subject' })
      }
      if (!node.config?.contentHtml?.trim()) {
        issues.push({ nodeId: node.id, message: 'Email step needs content' })
      }
    }
    if (node.type === 'WAIT') {
      const { days = 0, hours = 0, minutes = 0, untilHour } = node.config ?? {}
      const total = days * 1440 + hours * 60 + minutes
      if (total <= 0 && untilHour === undefined) {
        issues.push({ nodeId: node.id, message: 'Wait step needs a duration' })
      }
    }
    if (node.type === 'BRANCH') {
      const config = node.config
      if (!config) {
        issues.push({
          nodeId: node.id,
          message: 'Branch step needs a condition',
        })
      } else if (
        (config.kind === 'EMAIL_OPENED' || config.kind === 'EMAIL_CLICKED') &&
        (!config.windowHours || config.windowHours <= 0)
      ) {
        issues.push({
          nodeId: node.id,
          message: 'Branch step needs a wait window',
        })
      } else if (config.kind === 'CONTACT_QUERY' && !config.query?.trim()) {
        issues.push({ nodeId: node.id, message: 'Branch step needs a query' })
      }
    }
  }

  return issues
}
